import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, cleanParams } from '@/api/client'
import type {
  Availability,
  Cover,
  CoverCandidates,
  CoverKind,
  CoverNeed,
  CoverPayload,
  Lesson,
  Period,
  Room,
  RoomKind,
  RoomWeek,
  SectionGrid,
  SlotPayload,
  StaffTimetable,
  TeacherWeek,
} from './types'

export const timetableKeys = {
  all: ['timetable'] as const,
  periods: () => [...timetableKeys.all, 'periods'] as const,
  rooms: () => [...timetableKeys.all, 'rooms'] as const,
  section: (id: number) => [...timetableKeys.all, 'section', id] as const,
  teacher: (id: number) => [...timetableKeys.all, 'teacher', id] as const,
  room: (id: number) => [...timetableKeys.all, 'room', id] as const,
  availability: (params: object) => [...timetableKeys.all, 'availability', params] as const,
  mine: () => [...timetableKeys.all, 'me', 'staff'] as const,
  child: (studentId: number) => [...timetableKeys.all, 'me', 'family', studentId] as const,
  covers: (when: 'current' | 'past') => [...timetableKeys.all, 'covers', when] as const,
  needs: (params: object) => [...timetableKeys.all, 'covers', 'needs', params] as const,
  candidates: (params: object) => [...timetableKeys.all, 'covers', 'candidates', params] as const,
}

export const periodsQuery = () =>
  queryOptions({ queryKey: timetableKeys.periods(), queryFn: () => api.get<Period[]>('/timetable/periods') })
export const roomsQuery = () =>
  queryOptions({ queryKey: timetableKeys.rooms(), queryFn: () => api.get<Room[]>('/timetable/rooms') })
export const sectionTimetableQuery = (sectionId: number) =>
  queryOptions({
    queryKey: timetableKeys.section(sectionId),
    queryFn: () => api.get<SectionGrid>(`/timetable/sections/${sectionId}`),
  })
export const teacherTimetableQuery = (staffId: number) =>
  queryOptions({
    queryKey: timetableKeys.teacher(staffId),
    queryFn: () => api.get<TeacherWeek>(`/timetable/teachers/${staffId}`),
  })
export const roomTimetableQuery = (roomId: number) =>
  queryOptions({
    queryKey: timetableKeys.room(roomId),
    queryFn: () => api.get<RoomWeek>(`/timetable/rooms/${roomId}`),
  })
export const availabilityQuery = (params: {
  dayOfWeek: number
  periodId: number
  excludeSectionId: number
  subjectId?: number
}) =>
  queryOptions({
    queryKey: timetableKeys.availability(params),
    queryFn: () => api.get<Availability>('/timetable/availability', { params }),
    staleTime: 0,
  })
export const myTimetableQuery = () =>
  queryOptions({
    queryKey: timetableKeys.mine(),
    queryFn: () => api.get<StaffTimetable>('/me/staff/timetable'),
  })
export const childTimetableQuery = (studentId: number) =>
  queryOptions({
    queryKey: timetableKeys.child(studentId),
    queryFn: () => api.get<{ grid: SectionGrid | null }>(`/me/family/students/${studentId}/timetable`),
  })

export const coversQuery = (when: 'current' | 'past') =>
  queryOptions({
    queryKey: timetableKeys.covers(when),
    queryFn: () => api.get<Cover[]>('/timetable/covers', { params: { when } }),
  })
export const coverNeedsQuery = (params: { days?: number; leaveRequestId?: number }) =>
  queryOptions({
    queryKey: timetableKeys.needs(params),
    queryFn: () => api.get<CoverNeed[]>('/timetable/covers/needs', { params: cleanParams(params) }),
  })
export const coverCandidatesQuery = (params: {
  kind: CoverKind
  sectionId: number
  subjectId?: number
  startDate: string
  endDate: string
}) =>
  queryOptions({
    queryKey: timetableKeys.candidates(params),
    queryFn: () => api.get<CoverCandidates>('/timetable/covers/candidates', { params: cleanParams(params) }),
    staleTime: 0,
  })

/** Any timetable write can change grids, weeks, availability and usage counts: refresh the area. */
function useTimetableMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void qc.invalidateQueries({ queryKey: timetableKeys.all }),
  })
}

/** Covers also change who sees which section in the staff portal. */
function useCoverMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: timetableKeys.all })
      void qc.invalidateQueries({ queryKey: ['portal', 'staff'] })
    },
  })
}

export const useCreateCover = () =>
  useCoverMutation((body: CoverPayload) => api.post<Cover>('/timetable/covers', body))
export const useRemoveCover = () => useCoverMutation((id: number) => api.delete(`/timetable/covers/${id}`))

export const useSaveSlot = (sectionId: number) =>
  useTimetableMutation((body: SlotPayload) => api.put<Lesson>(`/timetable/sections/${sectionId}/slots`, body))
export const useClearSlot = (sectionId: number) =>
  useTimetableMutation(({ dayOfWeek, periodId }: { dayOfWeek: number; periodId: number }) =>
    api.delete(`/timetable/sections/${sectionId}/slots/${dayOfWeek}/${periodId}`),
  )
export const useCopyTimetable = (sectionId: number) =>
  useTimetableMutation((sourceSectionId: number) =>
    api.post<{ copied: number; skipped: number }>(`/timetable/sections/${sectionId}/copy-from`, {
      sourceSectionId,
    }),
  )

export interface PeriodPayload {
  name: string
  startTime: string
  endTime: string
  isBreak: boolean
  shiftId: number | null
}
export const useSavePeriod = () =>
  useTimetableMutation(({ id, ...body }: PeriodPayload & { id?: number }) =>
    id ? api.put<Period>(`/timetable/periods/${id}`, body) : api.post<Period>('/timetable/periods', body),
  )
export const useDeletePeriod = () =>
  useTimetableMutation((id: number) => api.delete(`/timetable/periods/${id}`))

export interface RoomPayload {
  name: string
  kind: RoomKind
  capacity: number | null
  active: boolean
}
export const useSaveRoom = () =>
  useTimetableMutation(({ id, ...body }: RoomPayload & { id?: number }) =>
    id ? api.put<Room>(`/timetable/rooms/${id}`, body) : api.post<Room>('/timetable/rooms', body),
  )
export const useDeleteRoom = () => useTimetableMutation((id: number) => api.delete(`/timetable/rooms/${id}`))
